import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useFeatureFlags } from 'libs.flags.react';
import { formatStudyString, Link, LocaleContext, Text, useDateTimeFormat, useLocalizeMessage, } from 'libs.nucleus';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { OrgContext } from '../../contexts/org';
import { SiteContext } from '../../contexts/site';
import { getArchiveBannerSiteId } from '../../helpers/archive_banner.helpers';
import { getParticipantNextVisitWindow, getParticipantNumber, getParticipantStatus, } from '../../helpers/participants.helpers';
import { useArchiveBannerSummary } from '../../hooks/use_archive_banner_summary';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
const ARCHIVE_ROW_ACCENT_STYLE = {
    marginLeft: -24,
    marginTop: -8,
    marginBottom: -8,
    paddingLeft: 24,
    paddingTop: 8,
    paddingBottom: 8,
    boxShadow: 'inset 3px 0 0 0 #CA8A04',
};
export const useParticipantsTableColumns = () => {
    const translate = useLocalizeMessage();
    const { isFeatureEnabled } = useFeatureFlags();
    const isDocGenEconsentEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.DOC_GEN_ECONSENT);
    const { environment } = useCurrentEnvironment();
    const { site } = useContext(SiteContext);
    const { data: archiveSummary } = useArchiveBannerSummary({
        environment,
        siteId: getArchiveBannerSiteId(environment, site?._id),
        enabled: isDocGenEconsentEnabled,
    });
    const { formatDateTime, formatPlainDate } = useDateTimeFormat();
    const { participantStatusMap } = useContext(OrgContext);
    const currentLocale = useContext(LocaleContext);
    const archiveLinesByParticipantId = useMemo(() => {
        const linesByParticipantId = new Map();
        (archiveSummary?.byParticipant ?? []).forEach((row) => {
            linesByParticipantId.set(row.participantId, row.lines);
        });
        return linesByParticipantId;
    }, [archiveSummary]);
    const participantsColumns = useMemo(() => {
        return [
            {
                header: translate('label.participant.number'),
                accessorKey: 'c_number',
                cell: (context) => {
                    const row = context.row.original;
                    const hasArchiveLines = isDocGenEconsentEnabled && archiveLinesByParticipantId.has(row._id);
                    return (_jsx("div", { "data-testid": hasArchiveLines ? 'participant-archive-row-accent' : undefined, style: hasArchiveLines ? ARCHIVE_ROW_ACCENT_STYLE : undefined, children: _jsx(Link, { LinkComponent: RouterLink, to: `/site/study/${environment?.id}/participants/${row._id}`, children: getParticipantNumber(row) }) }));
                },
            },
            {
                header: translate('generic.create', { tense: 'past' }),
                accessorKey: 'created',
                cell: (context) => formatDateTime(context.getValue()),
            },
            {
                header: translate('label.participants_table.next_visit_window'),
                accessorKey: 'c_visit_schedule',
                cell: (context) => {
                    return getParticipantNextVisitWindow(context.row.original, formatPlainDate, translate);
                },
            },
            {
                header: translate('generic.status'),
                accessorKey: 'c_status',
                cell: (context) => {
                    const status = context.getValue();
                    const statusLabel = participantStatusMap.get(status) || status;
                    const archiveLines = isDocGenEconsentEnabled
                        ? archiveLinesByParticipantId.get(context.row.original._id)
                        : undefined;
                    const archiveLine = !archiveLines?.length
                        ? undefined
                        : archiveLines.length === 1
                            ? archiveLines[0]
                            : translate('info.participants_table.archived_count', { count: archiveLines.length });
                    return (_jsxs("div", { className: 'flex items-center gap-2', children: [getParticipantStatus(formatStudyString(statusLabel, currentLocale)), archiveLine ? (_jsx(Text, { size: 'xs', className: 'nucleus-text-text-warning', dataTestId: 'participant-archive-line', children: archiveLine })) : null] }));
                },
            },
        ];
    }, [
        archiveLinesByParticipantId,
        currentLocale,
        environment?.id,
        formatDateTime,
        formatPlainDate,
        isDocGenEconsentEnabled,
        participantStatusMap,
        translate,
    ]);
    return {
        participantsColumns,
    };
};
